import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  ButtonProps,
  Card,
  CardBody,
  Center,
  Flex,
  Grid,
  HStack,
  Text,
  Textarea,
  Tooltip,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocalStorage } from "usehooks-ts";

import { Restricted } from "@/app/components/Restricted";
import { ChangeActorDisplay } from "@/common/components/ChangeActorDisplay";
import { ConfirmationDialog } from "@/common/components/ConfirmationDialog";
import EmptyState from "@/common/components/EmptyState";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import MotionBox from "@/common/components/MotionBox";
import { RulesDisplay } from "@/common/components/Rule/RulesForm";
import { SectionHeading } from "@/common/components/SectionHeading";
import TimestampCell from "@/common/components/TimestampCell";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import { AccessSummary } from "@/feature/components/AccessSummary";
import { useFeatureTargetingData } from "@/feature/data/useFeatureTargetingData";
import { useFeatureTargetingHistoryData } from "@/feature/data/useFeatureTargetingHistoryData";
import { useFeatureTargetingUpdateMutation } from "@/feature/data/useFeatureTargetingUpdateMutation";
import { ErrorResponse } from "@reflag/shared/api";
import { FeatureDetail } from "@reflag/shared/featureAPI";
import {
  FeatureTargetingMode,
  FeatureTargetingVersion,
} from "@reflag/shared/featureTargetingAPI";

function VersionAction({
  version,
  onRollback,
}: {
  version: FeatureTargetingVersion;
  onRollback: () => void;
}) {
  if (version.currentVersion) {
    return (
      <Badge colorScheme="brand" flexShrink={0} variant="subtle">
        Current
      </Badge>
    );
  }
  return (
    <RollbackButton
      color="dimmed"
      flexShrink={0}
      version={version}
      onRollback={onRollback}
    />
  );
}

function DiffCard({
  entities,
}: {
  entities: NonNullable<
    FeatureTargetingVersion["targetingSummaryChange"]
  >["addedEntities"];
}) {
  return (
    <Card>
      <CardBody p={4}>
        <AccessSummary targetingSummary={entities} hideSeparator wrap />
      </CardBody>
    </Card>
  );
}

function targetingModeLabel(mode: FeatureTargetingMode) {
  switch (mode) {
    case "none":
      return "No one";
    case "some":
      return "Some";
    case "everyone":
      return "Everyone";
  }
}

type TargetingSummaryChange = NonNullable<
  FeatureTargetingVersion["targetingSummaryChange"]
>;

function hasTargetingRuleChanges(summaryChange: TargetingSummaryChange) {
  return (
    summaryChange.addedEntities.length > 0 ||
    summaryChange.removedEntities.length > 0
  );
}

function hasTargetingModeChange(summaryChange: TargetingSummaryChange) {
  return summaryChange.oldTargetingMode !== summaryChange.newTargetingMode;
}

function hasVisibleTargetingChange(summaryChange: TargetingSummaryChange) {
  return (
    hasTargetingRuleChanges(summaryChange) ||
    hasTargetingModeChange(summaryChange)
  );
}

function TargetingModeChange({
  newTargetingMode,
  oldTargetingMode,
}: {
  oldTargetingMode: FeatureTargetingMode;
  newTargetingMode: FeatureTargetingMode;
}) {
  return (
    <HStack color="dimmed" fontSize="sm" spacing={2} wrap="wrap">
      <Text as="span">Targeting mode</Text>
      <Badge variant="subtle">{targetingModeLabel(oldTargetingMode)}</Badge>
      <Text as="span">→</Text>
      <Badge variant="subtle">{targetingModeLabel(newTargetingMode)}</Badge>
    </HStack>
  );
}

function TargetingRuleChange({
  summaryChange,
  version,
  onRollback,
}: {
  summaryChange: TargetingSummaryChange;
  version: FeatureTargetingVersion;
  onRollback: () => void;
}) {
  const hasAdded = summaryChange.addedEntities.length > 0;
  const hasRemoved = summaryChange.removedEntities.length > 0;

  if (hasAdded && hasRemoved) {
    return (
      <Grid
        alignItems="start"
        columnGap={3}
        rowGap={1}
        templateColumns="1fr 1fr"
      >
        <Text color="green.500" fontSize="xs" fontWeight="semibold">
          Added
        </Text>
        <Flex align="center" justify="space-between">
          <Text color="red.400" fontSize="xs" fontWeight="semibold">
            Removed
          </Text>
          <VersionAction version={version} onRollback={onRollback} />
        </Flex>
        <DiffCard entities={summaryChange.addedEntities} />
        <DiffCard entities={summaryChange.removedEntities} />
      </Grid>
    );
  }

  return (
    <Flex direction="column" gap={1}>
      <Flex align="center" justify="space-between">
        <Text
          color={hasAdded ? "green.500" : "red.400"}
          fontSize="xs"
          fontWeight="semibold"
        >
          {hasAdded ? "Added" : "Removed"}
        </Text>
        <VersionAction version={version} onRollback={onRollback} />
      </Flex>
      <DiffCard
        entities={
          hasAdded ? summaryChange.addedEntities : summaryChange.removedEntities
        }
      />
    </Flex>
  );
}

function TargetingChange({
  summaryChange,
  version,
  onRollback,
}: {
  summaryChange: TargetingSummaryChange;
  version: FeatureTargetingVersion;
  onRollback: () => void;
}) {
  const hasRules = hasTargetingRuleChanges(summaryChange);
  const hasMode = hasTargetingModeChange(summaryChange);

  return (
    <Flex direction="column" gap={2}>
      {hasMode && (
        <Flex
          align="center"
          justify={hasRules ? "flex-start" : "space-between"}
        >
          <TargetingModeChange
            newTargetingMode={summaryChange.newTargetingMode}
            oldTargetingMode={summaryChange.oldTargetingMode}
          />
          {!hasRules && (
            <VersionAction version={version} onRollback={onRollback} />
          )}
        </Flex>
      )}

      {hasRules && (
        <TargetingRuleChange
          summaryChange={summaryChange}
          version={version}
          onRollback={onRollback}
        />
      )}
    </Flex>
  );
}

function RollbackButton({
  version,
  onRollback,
  ...rest
}: ButtonProps & {
  version: FeatureTargetingVersion;
  onRollback: () => void;
}) {
  return (
    <Restricted
      role={version.environment.isProduction ? "publisher" : "contributor"}
    >
      <Tooltip
        label={
          !version.isValid
            ? "Flag targeting rules are no longer valid."
            : undefined
        }
        openDelay={300}
        placement="top"
        hasArrow
      >
        <Button
          isDisabled={!version.isValid}
          size="xs"
          variant="ghost"
          onClick={onRollback}
          {...rest}
        >
          Rollback
        </Button>
      </Tooltip>
    </Restricted>
  );
}

type FeatureTargetingHistoryProps = {
  feature: FeatureDetail;
  environmentId: string;
};

type TargetingHistoryViewMode = "summary" | "diff";

const TargetingHistoryViewModeStorageKey = "reflag-targeting-history-view-mode";

export function FeatureTargetingHistory({
  feature,
  environmentId,
}: FeatureTargetingHistoryProps) {
  const {
    data: versions = [],
    isLoading: versionsAreLoading,
    isFetching: versionsAreFetching,
    isError: versionsLoadingError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFeatureTargetingHistoryData(environmentId, feature.id, {
    pageSize: 5,
    includeTargetingSummary: true,
  });

  const {
    data: featureTargeting = [],
    isLoading: featureTargetingIsLoading,
    isError: featureTargetingLoadingError,
  } = useFeatureTargetingData(feature.id);

  const errorToast = useErrorToast();
  const [viewMode, setViewMode] = useLocalStorage<string>(
    TargetingHistoryViewModeStorageKey,
    "summary",
  );

  useEffect(() => {
    if (featureTargetingLoadingError || versionsLoadingError) {
      errorToast({
        title: "Failed to load targeting versions",
      });
    }
  }, [featureTargetingLoadingError, versionsLoadingError, errorToast]);

  const isLoading = versionsAreLoading || featureTargetingIsLoading;
  const effectiveViewMode: TargetingHistoryViewMode =
    viewMode === "diff" ? "diff" : "summary";

  return (
    <Flex direction="column" gap={6} mt={8} w="full">
      <Flex align="center" justify="space-between">
        <SectionHeading isLoading={isLoading || versionsAreFetching}>
          Targeting rules timeline
        </SectionHeading>
        <ButtonGroup isAttached size="sm" variant="outline">
          <Button
            isActive={viewMode !== "diff"}
            onClick={() => setViewMode("summary")}
          >
            Snapshot
          </Button>
          <Button
            isActive={viewMode === "diff"}
            onClick={() => setViewMode("diff")}
          >
            Changes
          </Button>
        </ButtonGroup>
      </Flex>
      {!isLoading && (
        <Log
          currentTargeting={featureTargeting}
          feature={feature}
          hasNextPage={hasNextPage}
          isLoadingMore={isFetchingNextPage}
          versions={versions}
          viewMode={effectiveViewMode}
          onLoadMore={fetchNextPage}
        />
      )}
    </Flex>
  );
}

type LogProps = {
  feature: FeatureDetail;
  versions: FeatureTargetingVersion[];
  currentTargeting: FeatureTargetingVersion[];
  onLoadMore: () => void;
  hasNextPage?: boolean;
  isLoadingMore: boolean;
  viewMode: TargetingHistoryViewMode;
};

function Log({
  feature,
  versions,
  currentTargeting,
  onLoadMore,
  hasNextPage,
  isLoadingMore,
  viewMode,
}: LogProps) {
  const toast = useToast();
  const errorToast = useErrorToast();
  const [rollbackVersion, setRollbackVersion] = useState<
    FeatureTargetingVersion | undefined
  >();

  const timelineLineColor = useColorModeValue("gray.150", "gray.700");

  return (
    <>
      {versions.length === 0 ? (
        <Center flexGrow={1} my={8}>
          <EmptyState
            description="No targeting history to show yet"
            flexGrow={1}
          />
        </Center>
      ) : (
        <Flex
          direction="column"
          gap={8}
          isolation="isolate"
          position="relative"
        >
          <AnimatePresence initial={false}>
            {versions.map((version, index) => {
              const summaryChange = version.targetingSummaryChange;
              const showTargetingChange =
                viewMode === "diff" &&
                !!summaryChange &&
                hasVisibleTargetingChange(summaryChange);

              return (
                <MotionBox
                  key={version.id}
                  animate={{ opacity: 1, y: 0, scaleY: 1 }}
                  display="flex"
                  gap={4}
                  initial={{ opacity: 0, y: -10, scaleY: 0.9 }}
                  layout="position"
                  position="relative"
                  transition={{ duration: 0.25, type: "spring", bounce: 0.3 }}
                >
                  {index !== versions.length - 1 && (
                    <Box
                      borderLeftColor={timelineLineColor}
                      borderLeftStyle="solid"
                      borderLeftWidth={1}
                      bottom={-9}
                      left={1.5}
                      position="absolute"
                      top={1}
                    />
                  )}

                  <Box
                    alignSelf="flex-start"
                    bg="appBackground"
                    borderColor={timelineLineColor}
                    borderRadius="full"
                    borderStyle="solid"
                    borderWidth={1}
                    boxSize={3}
                    flexShrink={0}
                    mt="3px"
                    zIndex={1}
                  />

                  <Flex
                    direction={{ base: "column", md: "row" }}
                    flex={1}
                    gap={4}
                  >
                    <Flex
                      direction="column"
                      gap={3}
                      w={{ base: "auto", md: 48 }}
                    >
                      <HStack align="center" spacing={2}>
                        <Text
                          fontFamily="code"
                          fontSize="sm"
                          fontWeight="medium"
                        >
                          v{version.version}
                        </Text>
                        <Text color="dimmed" fontSize="xs" noOfLines={1}>
                          <TimestampCell value={version.createdAt} />
                        </Text>
                      </HStack>

                      {version.createdBy && (
                        <ChangeActorDisplay
                          actor={version.createdBy}
                          color="dimmed"
                          fontSize="xs"
                        />
                      )}
                    </Flex>

                    <Flex direction="column" flex={1} gap={3}>
                      {showTargetingChange && summaryChange ? (
                        <TargetingChange
                          summaryChange={summaryChange}
                          version={version}
                          onRollback={() => setRollbackVersion(version)}
                        />
                      ) : (
                        <Card>
                          <CardBody
                            alignItems="center"
                            display="flex"
                            justifyContent="space-between"
                            p={4}
                          >
                            {viewMode === "summary" ? (
                              <AccessSummary
                                targetingSummary={version.targetingSummary}
                              />
                            ) : (
                              <Text color="dimmed" fontSize="sm">
                                No targeting rule changes
                              </Text>
                            )}
                            <VersionAction
                              version={version}
                              onRollback={() => setRollbackVersion(version)}
                            />
                          </CardBody>
                        </Card>
                      )}

                      {version.changeDescription && (
                        <Text
                          color="dimmed"
                          fontSize="sm"
                          fontStyle="italic"
                          px={4}
                        >
                          &ldquo;{version.changeDescription}&rdquo;
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                </MotionBox>
              );
            })}
          </AnimatePresence>

          {/* Load More Button */}
          {hasNextPage && (
            <Center mt={4}>
              <Button
                isLoading={isLoadingMore}
                loadingText="Loading more..."
                size="sm"
                variant="outline"
                onClick={onLoadMore}
              >
                Load more
              </Button>
            </Center>
          )}
        </Flex>
      )}

      {rollbackVersion && (
        <RollbackConfirm
          currentTargeting={currentTargeting}
          feature={feature}
          rollbackVersion={rollbackVersion}
          onCancel={() => setRollbackVersion(undefined)}
          onError={(error) => {
            errorToast({
              title: "Targeting version roll back failed",
              description:
                error?.error.code === "not_possible"
                  ? "The current targeting version has changed, refresh the flag history and try again."
                  : error?.error?.message,
            });
          }}
          onSuccess={() => {
            toast({
              title: "Targeting version rolled back",
              status: "success",
            });

            setRollbackVersion(undefined);
          }}
        />
      )}
    </>
  );
}

type RollbackConfirmProps = {
  feature: FeatureDetail;
  currentTargeting: FeatureTargetingVersion[];
  rollbackVersion: FeatureTargetingVersion;
  onCancel: () => void;
  onSuccess: (newVersion: FeatureTargetingVersion) => void;
  onError: (error: ErrorResponse | undefined) => void;
};

function RollbackConfirm({
  feature,
  currentTargeting,
  rollbackVersion,
  onCancel,
  onSuccess,
  onError,
}: RollbackConfirmProps) {
  const form = useForm({
    defaultValues: {
      changeDescription: `Rolled back to version #${rollbackVersion?.version}`,
    },
  });

  useEffect(() => {
    form.reset({
      changeDescription: `Rolled back to version #${rollbackVersion?.version}`,
    });
  }, [rollbackVersion, form]);

  const rollbackMutation = useFeatureTargetingUpdateMutation(
    feature.id,
    currentTargeting.map((v) => v.id),
  );

  return (
    <ConfirmationDialog
      _contentProps={{ maxW: "2xl" }}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      confirmLabel="Roll back"
      description={
        <FormProvider {...form}>
          <Text>
            Are you sure you want to roll back to{" "}
            <strong>version #{rollbackVersion?.version}</strong>?
          </Text>
          <Text>
            A new version will created based on the following rollout
            conditions:
          </Text>
          <RulesDisplay flagKey={feature.key} targeting={rollbackVersion} />
          <ManagedFormControl
            label="Add a note"
            name="changeDescription"
            render={({ field }) => (
              <Textarea
                {...field}
                placeholder="Increased rollout percentage after positive feedback from beta testers and no significant technical issues."
              />
            )}
          />
        </FormProvider>
      }
      isLoading={rollbackMutation.isPending}
      isOpen={Boolean(rollbackVersion)}
      title={`Roll back to version #${rollbackVersion?.version}`}
      onCancel={onCancel}
      onClose={onCancel}
      onConfirm={() => {
        rollbackMutation.mutate(
          {
            changeDescription: form.getValues("changeDescription"),
            versions: [
              {
                targetingMode: rollbackVersion?.targetingMode,
                segmentIds: rollbackVersion?.segmentIds,
                companyIds: rollbackVersion?.companyIds,
                userIds: rollbackVersion?.userIds,
                environmentId: rollbackVersion?.environment.id,
                customRules: rollbackVersion?.customRules,
              },
            ],
          },
          {
            onError: (error) => onError(error.response?.data),
            onSuccess: (flagVersions) => {
              onSuccess(flagVersions[0]);
            },
          },
        );
      }}
    />
  );
}
